import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';
import { useOrg } from '../contexts/OrgContext';
import { usePerspective } from '../contexts/PerspectiveContext';
import { normalizeUserType } from '../utils/userType';
import type { User } from '../types/user';

interface ProtectedRouteProps {
  children: ReactElement;
  requiredUserType?: string;
  requireOrg?: boolean;
  requireSuperAdmin?: boolean;
  currentUser: User | null;
  isLoading?: boolean;
}

// Canonical super-admin predicate — mirrors usePermissions().isSuperAdmin so the
// route gate and the UI gate agree exactly.
const isSuperAdminUser = (user: User): boolean =>
  user.isSuperAdmin === true ||
  (typeof user.organization === 'string' && user.organization.toUpperCase() === 'BOTH');

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredUserType,
  requireOrg = false,
  requireSuperAdmin = false,
  currentUser,
  isLoading = false
}) => {
  const { org } = useOrg(); // always 'epl'
  const { isInSwitchedView, perspectiveContext } = usePerspective();
  const location = useLocation();
  const currentUserType = normalizeUserType(currentUser?.userType);
  const requiredType = normalizeUserType(requiredUserType);

  // Show a loading skeleton while the session is restoring
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={64} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Check authentication first
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Check user type if required
  // Allow access if admin is in switched view and the route matches the switched perspective
  if (requiredUserType && currentUserType !== requiredType) {
    // Check if admin is in switched view and trying to access the matching perspective route
    if (isInSwitchedView && perspectiveContext) {
      const switchedUserType = normalizeUserType(perspectiveContext.currentPerspective);
      // Allow if the switched perspective matches the required user type
      if (switchedUserType === requiredType) {
        // Continue to render children - admin is viewing as this user type
      } else {
        return <Navigate to="/" replace />;
      }
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // Super-admin-only routes (e.g. Marketing, equicenta Study, Sales Hub). Checked
  // BEFORE org handling so the "org not yet hydrated" early-return below can't be
  // used to slip past the gate via a direct URL. Bounce non-super-admins home.
  if (requireSuperAdmin && !isSuperAdminUser(currentUser)) {
    return <Navigate to={org ? `/internal/${org}/dashboard` : '/'} replace />;
  }

  // EPL is the only organization. Redirect any legacy /internal/bare/* deep
  // link (old bookmarks, emailed links) to the equivalent EPL path so nothing
  // 404s now that the BARE routes have been removed.
  if (requireOrg && location.pathname.startsWith('/internal/bare/')) {
    const eplPath = location.pathname.replace('/internal/bare/', '/internal/epl/');
    return <Navigate to={eplPath} replace />;
  }
  if (requireOrg && location.pathname === '/internal/bare') {
    return <Navigate to="/internal/epl/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
